import { actions as webdriverCommandActions } from '@saucelabs/sl-module-webdriver-commands';
import { actions as espressoCommandActions } from '@saucelabs/sl-module-espresso-commands';
import { actions as buildsActions } from '@saucelabs/sl-module-builds';
import { actions as jobActions } from '@saucelabs/sl-module-jobs';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';
import { constants as notificationConstants } from '@saucelabs/sl-module-notifications';
import { utils as authUtils } from '@saucelabs/sl-container-authentication';
import { actions as modalActions } from '@saucelabs/sl-container-modal';
import { recordTrack } from '@saucelabs/sl-util-segmentio';
import { createApiAction } from '@saucelabs/sl-util-redux';
import { parse } from 'qs';
import {
  throttle as _throttle,
  last as _last,
  pick as _pick,
  values as _values,
} from 'lodash';
import { actions as performanceActions } from '@saucelabs/sl-module-performance';
import { actions as errorPageActions } from '@saucelabs/sl-container-error-pages';

import { actions as fileActions, constants as fileConstants, utils as fileUtils } from './../file';
import { actions as containerJobActions, operations as containerJobOperations } from './../job';
import * as actions from './actions';
import * as constants from './constants';
import * as types from './types';
import { newActiveCommand, hasUserAccessToTheJob } from './utils';
import { commandSelector, filteredCommandsSelector, playerSelector } from './selectors';

import { operations } from './index';


export const getJob = (payload, meta) => (dispatch) => (
  dispatch(jobActions.getJob(payload, meta))
    .then(({ data }) => {
      if (data.error) {
        const notification = dispatch(notificationActions.notifyWarning(
          data.error,
          'There is a problem with your test.',
          true,
        ));
        dispatch(actions.appendNotification(notification.payload.id));
      }
      return data;
    })
);

export const getAsset = (payload) => (dispatch) => (
  dispatch(jobActions.getAsset(payload, { skipErrorPage: true }))
);

export const getRdcAsset = (payload) => (dispatch) => (
  dispatch(jobActions.getRdcAsset(payload, { skipErrorPage: true }))
    .catch(() => {
      const notification = dispatch(notificationActions.notifyDanger(
        payload.url, 'Failed to fetch test report asset.', true,
      ));
      dispatch(actions.appendNotification(notification.payload.id));
    })
);

export const fetchJob = (payload) => (dispatch) => (
  dispatch(operations.getJob(payload, { skipErrorPage: true }))
    .catch((slError) => (
      dispatch(jobActions.getUPJob(payload))
        .catch(() => {
          throw slError;
        })
    ))
);

export const initialize = createApiAction(
  types.INITIALIZE,
  (payload, meta, dispatch, getState) => {
    const initialState = getState();
    const {
      user,
      router: { location: { pathname: currentPathname, search } },
    } = initialState;
    const qs = parse(search, { ignoreQueryPrefix: true });
    const authInQS = !!qs.auth;

    return dispatch(operations.fetchJob(payload))
      .then((response) => {
        const job = getState().apps.job.job.get('job');
        if ((!user.isAuthenticated() && !authInQS) && !job.isPublic()) {
          throw authUtils.redirectToMainLogin(currentPathname);
        }

        if (!hasUserAccessToTheJob({ user, job })) {
          return dispatch(errorPageActions.showNotFoundPage());
        }

        dispatch(actions.recordTrackViewed(
          response.base_config,
          { id: response.id, error: response.error },
        ));
        return response;
      }).catch((errResponse) => {
        dispatch(actions.recordTrackViewed(
          errResponse.base_config,
          { id: errResponse.id, error: errResponse.error },
        ));
        if (!user.isAuthenticated()) {
          throw authUtils.redirectToMainLogin(currentPathname);
        }
        throw errResponse;
      });
  },
);

// If job has commands, returns the filename of commands log
// Otherwise, returns the filename of first found log
export const getPrimaryLogFilename = (job, assets) => {
  if (job.hasCommands()) {
    if (job.isEspresso()) {
      return fileConstants.ESPRESSO_LOGFILE_NAME;
    }
    return fileConstants.SELENIUM_LOGFILE_NAME;
  }

  const logFileEntries = fileUtils.logFilesOnly(assets);
  if (!logFileEntries.length) {
    return null;
  }
  const [[, firstLogFilename]] = logFileEntries;
  return firstLogFilename;
};

export const parseLogfile = (job, logData) => (dispatch) => {
  if (job.isEspresso()) {
    return dispatch(espressoCommandActions.parseEspressoLog(logData));
  }
  return dispatch(webdriverCommandActions.parseSeleniumLog(logData));
};

export const tryGetBuildByName = (job) => (dispatch) => {
  if (!job.getBuildName()) {
    return Promise.resolve();
  }

  return dispatch(buildsActions.getBuildByName({
    buildName: job.getBuildName(),
    username: job.get('owner'),
  }, { skipErrorPage: true }));
};

export const fetchConsoleLogAsset = ({ job }) => (dispatch) => {
  if (job.isExtendedDebugging() && job.hasExtendedDebuggingSupport()) {
    dispatch(jobActions.setAssetList({ 'console.json': 'console.json' }));
  }
};

let tryGetSLAssetsTimeout;
export const tryGetSLAssets = ({ auth, job, config }) => (dispatch) => {
  if (!job.showAssets()) {
    clearTimeout(tryGetSLAssetsTimeout);
    dispatch(containerJobActions.assetsExpired());
    const notification = dispatch(notificationActions.notifyDanger(
      constants.MISSING_ASSETS_MESSAGE, 'Test assets have expired.', true,
    ));
    dispatch(actions.appendNotification(notification.payload.id));
    return Promise.resolve();
  }

  const { id } = job;
  return dispatch(jobActions.getAssetsList({ id, config }, { skipErrorPage: true }))
    .then(({ data }) => {
      clearTimeout(tryGetSLAssetsTimeout);
      const logfileName = operations.getPrimaryLogFilename(job, data);
      dispatch(operations.fetchConsoleLogAsset({ job }));
      return dispatch(operations.getAsset(
        { id, auth, assetName: logfileName, config },
        { skipErrorPage: true },
      ))
        .then((logFile) => {
          dispatch(fileActions.attachFile({ name: logfileName, data: logFile.data }));
          dispatch(fileActions.selectFile(logfileName));
          if (job.hasCommands()) {
            dispatch(operations.parseLogfile(job, logFile.data));
          }
        });
    })
    .catch(({ response }) => {
      if (response && response.data && response.data === constants.ASSETS_NOT_AVAILABLE_YET) {
        tryGetSLAssetsTimeout = setTimeout(() => {
          dispatch(tryGetSLAssets({ job, config }));
        }, constants.API_FETCH_INTERVAL);
      }
    });
};

export const fetchSLJobAdditionalData = ({ auth, job, config }) => (dispatch) => {
  dispatch(operations.tryGetBuildByName(job));
  return dispatch(operations.tryGetSLAssets({ auth, job, config }));
};

export const fetchRdcJobAdditionalData = ({ job }) => (dispatch) => {
  const urls = _pick(job, ['device_log_url', 'framework_log_url', 'requests_url']);
  const requests = _values(urls).map((url) => dispatch(getRdcAsset({ url })));

  return Promise.all(requests).then(
    ([deviceLog, frameworkLog, deviceRequests]) => {
      const assetList = {};

      if (job.video_url) {
        assetList.video = job.video_url;
      }

      if (deviceRequests && deviceRequests.data) {
        assetList['commands.json'] = 'commands.json';
        dispatch(fileActions.attachFile({
          name: 'commands.json',
          data: deviceRequests.data,
        }));
        dispatch(webdriverCommandActions.parseAppiumLog(deviceRequests.data));
      }

      if (deviceLog && deviceLog.data) {
        assetList['device.log'] = 'device.log';
        dispatch(fileActions.attachFile({
          name: 'device.log',
          data: fileActions.formatRdcLogFile(deviceLog.data),
        }));
      }

      if (frameworkLog && frameworkLog.data) {
        assetList['appium.log'] = 'appium.log';
        dispatch(fileActions.attachFile({
          name: 'appium.log',
          data: fileActions.formatRdcLogFile(frameworkLog.data),
        }));
      }

      dispatch(jobActions.setAssetList(assetList));
      dispatch(fileActions.selectFile(_last(_values(assetList))));
    },
  );
};

export const fetchAdditionalData = ({ auth, job, config }) => (dispatch) => {
  dispatch(recordTrack(constants.MIXPANEL_EVENTS.PAGE_OPEN));
  if (job.isRdc()) {
    return dispatch(fetchRdcJobAdditionalData({ job }));
  }

  return dispatch(fetchSLJobAdditionalData({ auth, job, config }));
};

const throttledNotifyLinkCopied = _throttle(
  (dispatch) => dispatch(notificationActions.notify({
    category: notificationConstants.CATEGORY_SUCCESS,
    text: 'URL address is copied to your clipboard.',
  })),
  constants.NOTIFICATION_THROTTLE_INTERVAL,
  { trailing: false },
);

const throttledNotifyLinkFailedToCopy = _throttle(
  (dispatch) => dispatch(notificationActions.notify({
    category: notificationConstants.CATEGORY_SUCCESS,
    text: 'URL address is copied to your clipboard.',
  })),
  constants.NOTIFICATION_THROTTLE_INTERVAL,
  { trailing: false },
);

export const notifyLinkCopied = () => (dispatch) => throttledNotifyLinkCopied(dispatch);

export const notifyLinkFailedToCopy = () => (dispatch) => throttledNotifyLinkFailedToCopy(dispatch);

const throttledUpdateActiveCommand = _throttle(
  (dispatch, payload) => dispatch(containerJobOperations.updateActiveCommand(payload)),
  500,
  { trailing: false },
);

export const onVideoPlayerMounted = ({ player }) => (dispatch) => {
  dispatch(actions.videoPlayerMounted(player));

  player.on('timeupdate', () => (
    throttledUpdateActiveCommand(
      dispatch,
      { currentTime: player.currentTime() },
    )
  ));
};

export const videoJumpToCommand = (command) => (dispatch, getState) => {
  const player = playerSelector(getState());

  if (!player || typeof player.currentTime !== 'function') {
    return;
  }

  const timelineSeconds = command.getTimeline();
  const playerCurrentTime = player.currentTime();

  dispatch(actions.jumpToTime(timelineSeconds));
  player.pause();
  // if we return `timelineSeconds`, then it will start on the last milisecond of
  // previous command. Adding small offset to be sure we will select a proper command.
  player.currentTime(timelineSeconds + 0.0001);

  // we want to hide posterImage when somebody clicks on commands list item
  // when didn't play the video before
  if (playerCurrentTime === 0) {
    player.hasStarted(true);
  }
};

export const videoJumpToCommandId = (commandId) => (dispatch, getState) => (
  dispatch(operations.videoJumpToCommand(commandSelector(commandId)(getState())))
);

export const videoPlayPause = () => (dispatch, getState) => {
  const player = playerSelector(getState());

  if (player.paused()) {
    player.play();
  } else {
    player.pause();
  }
};

const navigateCommandByOffset = (offset) => (dispatch, getState) => {
  const commands = filteredCommandsSelector(getState());
  const currentActive = commands.findIndex((command) => command.active);
  const newActiveId = newActiveCommand(commands, offset, currentActive);

  dispatch(operations.videoJumpToCommandId(newActiveId));
  // Updating the video triggers active command update anyway, but it's too slow:
  dispatch(containerJobOperations.updateActiveCommand({
    collapseCurrent: true,
    commandId: newActiveId,
  }));
};

export const navigateCommandPrevious = () => navigateCommandByOffset(-1);
export const navigateCommandNext = () => navigateCommandByOffset(1);

export const setFilterPhrase = (phrase) => actions.updateCommandsFilter({ phrase });
export const setScreenshotOnly = (
  (screenshotOnly) => actions.updateCommandsFilter({ screenshotOnly })
);

export const cleanup = ({ notifications }) => (dispatch) => (
  Promise.all([
    dispatch(actions.cleanup()),
    dispatch(fileActions.cleanup()),
    dispatch(containerJobActions.cleanup()),
    dispatch(performanceActions.cleanup()),
    ...notifications.map((notificationId) => (
      dispatch(notificationActions.hideNotification(notificationId))
    )),
  ])
);

export const createLiveSessionPreviewNotification = (isFreeUser) => (dispatch) => {
  let description = 'Some features will be disabled until the test has finished running.';
  if (!isFreeUser) {
    description += ' You can click on video to interact and take over the running test';
  }

  const notification = dispatch(notificationActions.notifyInfo(
    description,
    'This test is currently running...',
    true,
  ));
  dispatch(actions.appendNotification(notification.payload.id));
  return () => (
    dispatch(notificationActions.hideNotification(notification.payload.id))
  );
};

export const reportJobIssue = (payload) => (dispatch) => (
  dispatch(actions.reportJobIssue(payload))
    .then(() => {
      dispatch(notificationActions.notifySuccess('Issue successfully reported!'));
      dispatch(modalActions.hideModal(constants.REPORT_JOB_ISSUE_MODAL));
    })
);
